import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  FormControlLabel,
  IconButton,
  Slider,
  Switch,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import AWS from "aws-sdk";
import Compressor from "compressorjs";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Booking from "../api/booking";
import MachineApi from "../api/machine";
import Language from "../constants/language_fr.json";

const {
  REACT_APP_LOKA_S3_APP_BUCKET,
  REACT_APP_S3_APP_REGION,
  REACT_APP_KEYPREFIX,
  REACT_APP_AWS_ACCESS_KEY_ID,
  REACT_APP_AWS_SECRET_ACCESS_KEY_ID,
} = process.env;

const EditChecking = ({
  checkingKey,
  contract,
  handleClose,
  handleMergeBooking,
  isOpen,
}) => {
  const contractCheckingKey =
    checkingKey === "startChecking"
      ? "contractsToStartCheckings"
      : "contractsToStopCheckings";
      
  const { user } = useSelector(({ slice }) => slice.value);
  const [config, setConfig] = useState(null);
  const [formValues, setFormValues] = useState(
    contract?.[contractCheckingKey] || {},
  );
  const [uploading, setUploading] = useState({});
  const [saving, setSaving] = useState(false);
  const fileInputRefs = useRef({});

  const category = contract?.machine?.machine?.category;
  const token = user?.token;

  useEffect(() => {
    const load = async () => {
      const { checkings: { items = [] } = {} } = category
        ? await MachineApi.Config({ category, token })
        : {};
      setConfig(items);
    };
    load();
  }, [category, token]);

  const handleChange = (key, value) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: { ...(prev?.[key] ?? {}), value },
    }));
  };

  const handleNotesChange = (key, notes) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: { ...(prev?.[key] ?? {}), notes },
    }));
  };

  const handlePictureUpload = (itemKey, file) => {
    setUploading((prev) => ({ ...prev, [itemKey]: true }));

    AWS.config.update({
      accessKeyId: REACT_APP_AWS_ACCESS_KEY_ID,
      secretAccessKey: REACT_APP_AWS_SECRET_ACCESS_KEY_ID,
    });

    const bucket = new AWS.S3({
      params: { Bucket: REACT_APP_LOKA_S3_APP_BUCKET },
      region: REACT_APP_S3_APP_REGION,
    });

    new Compressor(file, {
      maxHeight: 800,
      maxWidth: 800,
      success(result) {
        const _name = `${user?._id}_${Date.now()}.jpg`;
        const params = {
          ACL: "public-read",
          Body: result,
          Bucket: `${REACT_APP_LOKA_S3_APP_BUCKET}/${REACT_APP_KEYPREFIX}`,
          Key: _name,
          ContentType: "image",
        };
        bucket.putObject(params).send((err) => {
          setUploading((prev) => ({ ...prev, [itemKey]: false }));
          if (!err) {
            handleChange(
              itemKey,
              `https://loka-prod.s3.eu-west-3.amazonaws.com/UPLOADS/${_name}`,
            );
          }
        });
      },
      error() {
        setUploading((prev) => ({ ...prev, [itemKey]: false }));
      },
    });
  };

  const handleSave = async () => {
    setSaving(true);
    const { data } = await Booking.Update({
      booking: { ...contract, [contractCheckingKey]: formValues },
      token: user?.token,
    });
    const { booking } = data;
    handleMergeBooking({ booking });
    setSaving(false);
    handleClose();
  };

  const notesField = (key) => (
    <TextField
      label="Notes"
      value={formValues[key]?.notes ?? ""}
      onChange={(e) => handleNotesChange(key, e.target.value)}
      variant="outlined"
      size="small"
      multiline
      rows={2}
      sx={{ width: 220, flexShrink: 0 }}
    />
  );

  const renderField = (item) => {
    const { key, label, type, min = 0, max = 100, unit } = item;
    const { value } = formValues[key] ?? {};

    switch (type) {
      case "numeric":
        return (
          <Box
            key={key}
            sx={{ display: "flex", gap: 2, mb: 2, alignItems: "flex-start" }}
          >
            <TextField
              label={unit ? `${label} (${unit})` : label}
              type="number"
              value={value ?? ""}
              onChange={(e) => handleChange(key, e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            />
            {notesField(key)}
          </Box>
        );

      case "slider":
        return (
          <Box
            key={key}
            sx={{ display: "flex", gap: 2, mb: 3, alignItems: "flex-start" }}
          >
            <Box sx={{ flex: 1, px: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {label}
              </Typography>
              <Slider
                value={Number(value) || min}
                min={min}
                max={max}
                step={1}
                marks
                valueLabelDisplay="auto"
                onChange={(_, val) => handleChange(key, val)}
              />
            </Box>
            {notesField(key)}
          </Box>
        );

      case "switch":
        return (
          <Box
            key={key}
            sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}
          >
            <FormControlLabel
              label={label}
              sx={{ flex: 1 }}
              control={
                <Switch
                  checked={!!value}
                  onChange={(e) => handleChange(key, e.target.checked)}
                />
              }
            />
            {notesField(key)}
          </Box>
        );

      case "picture":
        return (
          <Box
            key={key}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              width: 160,
            }}
          >
            {value ? (
              <Box
                component="img"
                src={value}
                alt={label}
                sx={{
                  width: 160,
                  height: 110,
                  objectFit: "cover",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 160,
                  height: 110,
                  borderRadius: 1,
                  border: "1px dashed #bdbdbd",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.disabled",
                }}
              >
                <AddPhotoAlternateIcon />
              </Box>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              noWrap
              sx={{ maxWidth: 160 }}
            >
              {label}
            </Typography>
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              ref={(el) => (fileInputRefs.current[key] = el)}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handlePictureUpload(key, file);
                e.target.value = "";
              }}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  uploading[key] ? (
                    <CircularProgress size={14} />
                  ) : (
                    <AddPhotoAlternateIcon />
                  )
                }
                disabled={uploading[key]}
                onClick={() => fileInputRefs.current[key]?.click()}
              >
                {value ? "Modifier" : "Ajouter"}
              </Button>
              {value && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleChange(key, null)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <TextField
              label="Notes"
              value={formValues[key]?.notes ?? ""}
              onChange={(e) => handleNotesChange(key, e.target.value)}
              variant="outlined"
              size="small"
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        );

      default:
        return (
          <Box
            key={key}
            sx={{ display: "flex", gap: 2, mb: 2, alignItems: "flex-start" }}
          >
            <TextField
              label={label}
              value={value ?? ""}
              onChange={(e) => handleChange(key, e.target.value)}
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              size="small"
            />
            {notesField(key)}
          </Box>
        );
    }
  };

  return (
    <Dialog open={isOpen} fullWidth maxWidth="md" onClose={handleClose}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            sx={{ mr: 2 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {Language?.editChecking?.[checkingKey]}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {config === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {config
              .filter(({ type }) => type !== "picture")
              .map((item) => renderField(item))}
            {config.some(({ type }) => type === "picture") && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  mb: 3,
                }}
              >
                {config
                  .filter(({ type }) => type === "picture")
                  .map((item) => renderField(item))}
              </Box>
            )}
            <Button
              variant="contained"
              fullWidth
              startIcon={
                saving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              disabled={saving}
              onClick={handleSave}
              sx={{ mt: 1 }}
            >
              Enregistrer
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default EditChecking;
